import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Shield, Clock, DollarSign, Mail, Phone, MapPin } from 'lucide-react';
import { siteInfo, practiceAreas, whyChooseUs } from '../data';
import { submitContactForm } from '../api';
import './Home.css';

const iconMap = { Users, Shield, Clock, DollarSign };

export default function Home() {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      await submitContactForm(form);
      setStatus('success');
      setForm({ fullName: '', email: '', company: '', phone: '', service: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="home-hero">
        <div className="home-hero__bg" />
        <div className="container home-hero__content">
          <span className="home-hero__label">Excellence in Legal Process Outsourcing</span>
          <h1>
            Our <span className="text-accent">Legal Excellence</span> you can trust
          </h1>
          <p>
            We combine legal expertise with cutting-edge technology to provide cost-effective,
            accurate, and timely solutions that empower your legal team to focus on strategic priorities.
          </p>
          <Link to="/contact-us" className="btn btn-primary">
            Get Started <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* ===== PRACTICE AREAS ===== */}
      <section className="home-practice">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Our Practice Areas</span>
            <h2 className="section-title">
              Comprehensive legal services tailored to meet your specific needs
            </h2>
          </div>

          <div className="practice-grid">
            {practiceAreas.map((area, i) => (
              <Link to={`/${area.slug}`} key={area.slug} className="practice-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="practice-card__img">
                  <img src={area.image} alt={area.shortTitle} loading="lazy" />
                </div>
                <div className="practice-card__body">
                  <h3>{area.shortTitle} Paralegal Services</h3>
                  <p>{area.homeDescription}</p>
                  <span className="practice-card__link">
                    Learn More <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="home-why">
        <div className="container">
          <div className="home-why__grid">
            <div className="home-why__content">
              <span className="section-label">Why Choose Us</span>
              <h2 className="section-title">
                We deliver precise, reliable legal process support with unmatched consistency
              </h2>

              <div className="why-cards">
                {whyChooseUs.map((item) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <div key={item.title} className="why-card">
                      <div className="why-card__icon">
                        {Icon && <Icon size={24} />}
                      </div>
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Link to="/document-review-paralegal-services" className="btn btn-outline">
                Learn More
              </Link>
            </div>

            <div className="home-why__image">
              <img
                src="/img/why-choose.webp"
                alt="Why choose Bridge LPO"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRANSFORM CTA ===== */}
      <section className="home-transform">
        <div className="home-transform__bg" />
        <div className="container home-transform__content">
          <h2>Ready to Transform Your Legal Operations?</h2>
          <p>Join leading law firms and corporations who trust Bridge LPO</p>
          <Link to="/contact-us" className="btn btn-primary">
            Get Started <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section className="home-contact">
        <div className="container">
          <div className="text-center">
            <h2 className="section-title">Get in touch</h2>
            <p className="section-subtitle">
              Ready to streamline your legal processes? Contact us today for a consultation
              and discover how Bridge LPO can support your organization.
            </p>
          </div>

          <div className="contact-grid">
            {/* Info Side */}
            <div className="contact-info">
              <div className="contact-info__block">
                <h4>Contact Information</h4>
                <div className="contact-info__item">
                  <Mail size={18} />
                  <div>
                    <strong>Email</strong>
                    <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>
                  </div>
                </div>
                <div className="contact-info__item">
                  <Phone size={18} />
                  <div>
                    <strong>Phone</strong>
                    <a href={`tel:${siteInfo.phone}`}>{siteInfo.phoneDisplay}</a>
                  </div>
                </div>
              </div>
              <div className="contact-info__block">
                <h4>Business Hours</h4>
                <div className="contact-info__hours">
                  <p><strong>Mon - Fri:</strong> {siteInfo.businessHours.weekdays}</p>
                  <p><strong>Saturday:</strong> {siteInfo.businessHours.saturday}</p>
                  <p><strong>Sunday:</strong> {siteInfo.businessHours.sunday}</p>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div className="contact-form-wrapper">
              {status === 'success' ? (
                <div className="form-success">
                  <h3>Thank you!</h3>
                  <p>We've received your message and will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name *</label>
                      <input
                        id="fullName" name="fullName" type="text" required
                        value={form.fullName} onChange={handleChange}
                        placeholder="John Smith"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        id="email" name="email" type="email" required
                        value={form.email} onChange={handleChange}
                        placeholder="john@lawfirm.com"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company">Company</label>
                      <input
                        id="company" name="company" type="text"
                        value={form.company} onChange={handleChange}
                        placeholder="Your law firm"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone" name="phone" type="tel"
                        value={form.phone} onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="service">Service Interested In</label>
                    <select id="service" name="service" value={form.service} onChange={handleChange}>
                      <option value="">Select a service</option>
                      <option value="personal-injury">Personal Injury</option>
                      <option value="workers-comp">Workers Compensation</option>
                      <option value="employment-law">Employment Law</option>
                      <option value="bankruptcy">Bankruptcy</option>
                      <option value="family-law">Family Law</option>
                      <option value="immigration">Immigration</option>
                      <option value="document-review">Document Review</option>
                      <option value="contract-management">Contract Management</option>
                      <option value="legal-research">Legal Research & Writing</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message" name="message" rows={4}
                      value={form.message} onChange={handleChange}
                      placeholder="Tell us about your needs..."
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                  {status === 'error' && (
                    <p className="form-error">Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
